/* Atelier global chrome.
   - <atelier-header>: mobile menu drawer (full-screen panel under the header): open/close, aria-expanded,
     Esc, focus trap, page scroll lock, closes at >= 990px and when the bag opens. Its live search is
     <atelier-predictive-search> (assets/atelier-search.js).
   - <atelier-nav-item>: desktop nav dropdown (snippets/atelier-nav-panel.liquid): opens on hover (with intent
     delays), on the chevron button (click/Enter/Space) and stays open while focus is inside; Esc, an outside
     click or focus leaving closes it. One open at a time.
   - <atelier-cart-reviews>: the review swipe row in the cart drawer (current card, click/drag to move). */
(() => {
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  // Focusable and actually shown: skips links inside a closed <details> (the accordion animates its content
  // box, so those links can still have layout boxes) and anything hidden.
  const visible = (el) => {
    if (el.getAttribute('tabindex') === '-1') return false; // e.g. search result rows (reached with the arrow keys)
    const closed = el.closest('details:not([open])');
    if (closed && !(el.tagName === 'SUMMARY' && el.parentElement === closed)) return false;
    if (el.checkVisibility) return el.checkVisibility({ visibilityProperty: true });
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  };
  const onMedia = (mql, fn) => (mql.addEventListener ? mql.addEventListener('change', fn) : mql.addListener(fn));
  const offMedia = (mql, fn) => (mql.removeEventListener ? mql.removeEventListener('change', fn) : mql.removeListener(fn));

  if (!customElements.get('atelier-header')) {
    class AtelierHeader extends HTMLElement {
      connectedCallback() {
        this.detectTone();
        this.toggle = this.querySelector('[data-at-menu-toggle]');
        this.drawer = this.querySelector('[data-at-drawer]');
        this.bar = this.querySelector('.at-header__bar');
        if (!this.toggle || !this.drawer) return;

        this.onToggle = (event) => {
          event.preventDefault();
          this.isOpen() ? this.close() : this.open();
        };
        this.onKeydown = (event) => this.handleKeydown(event);
        this.onMedia = () => desktop.matches && this.close(true);
        this.onResize = () => this.isOpen() && this.place();
        this.onDrawerClick = (event) => {
          if (event.defaultPrevented) return;
          if (event.target.closest('a[href]')) this.close(true);
        };
        this.onBagClick = () => this.close(true);

        this.toggle.addEventListener('click', this.onToggle);
        this.drawer.addEventListener('click', this.onDrawerClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize);
        onMedia(desktop, this.onMedia);
        this.bag = document.getElementById('cart-icon-bubble');
        if (this.bag) this.bag.addEventListener('click', this.onBagClick);
      }

      // Pages can give the header a dark ground (VIP green, I found it gold). Read the header's own background and,
      // when it is dark, switch its text, search field and icons to ivory (.at-header--on-dark), so any page works.
      detectTone() {
        const m = (getComputedStyle(this).backgroundColor || '').match(/[\d.]+/g);
        if (!m || m.length < 3 || (m[3] !== undefined && +m[3] === 0)) return;
        const lin = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
        const lum = 0.2126 * lin(+m[0]) + 0.7152 * lin(+m[1]) + 0.0722 * lin(+m[2]);
        this.classList.toggle('at-header--on-dark', lum < 0.3);
      }

      disconnectedCallback() {
        if (!this.toggle || !this.drawer) return;
        this.toggle.removeEventListener('click', this.onToggle);
        this.drawer.removeEventListener('click', this.onDrawerClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);
        offMedia(desktop, this.onMedia);
        if (this.bag) this.bag.removeEventListener('click', this.onBagClick);
        document.documentElement.classList.remove('at-menu-open');
      }

      isOpen() {
        return this.toggle.getAttribute('aria-expanded') === 'true';
      }

      // The panel starts right under the visible bottom edge of the header bar.
      place() {
        const anchor = this.bar || this;
        const top = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom));
        this.drawer.style.setProperty('--at-drawer-top', `${top}px`);
      }

      setToggle(open) {
        this.toggle.setAttribute('aria-expanded', String(open));
        const label = open ? this.toggle.dataset.labelClose : this.toggle.dataset.labelOpen;
        if (label) this.toggle.setAttribute('aria-label', label);
      }

      open() {
        if (this.isOpen()) return;
        this.place();
        this.setToggle(true);
        this.drawer.hidden = false;
        requestAnimationFrame(() => requestAnimationFrame(() => this.drawer.classList.add('is-open')));
        document.documentElement.classList.add('at-menu-open');
        this.drawer.focus({ preventScroll: true });
      }

      close(silent) {
        if (!this.isOpen()) return;
        this.setToggle(false);
        this.drawer.classList.remove('is-open');
        const done = () => {
          if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
        };
        if (reduceMotion.matches) done();
        else {
          this.drawer.addEventListener('transitionend', done, { once: true });
          setTimeout(done, 420);
        }
        document.documentElement.classList.remove('at-menu-open');
        if (!silent) this.toggle.focus({ preventScroll: true });
      }

      handleKeydown(event) {
        if (!this.isOpen()) return;
        if (event.key === 'Escape') {
          if (event.defaultPrevented) return; // e.g. the search list closed first (assets/atelier-search.js)
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab') return;
        // Focus cycles through the menu button and everything inside the open panel.
        const items = [this.toggle, ...[...this.drawer.querySelectorAll(FOCUSABLE)].filter(visible)];
        if (items.length < 2) return;
        const index = items.indexOf(document.activeElement);
        event.preventDefault();
        let next;
        if (index === -1) next = event.shiftKey ? items.length - 1 : 1;
        else next = (index + (event.shiftKey ? -1 : 1) + items.length) % items.length;
        items[next].focus();
      }
    }
    customElements.define('atelier-header', AtelierHeader);
  }

  if (!customElements.get('atelier-nav-item')) {
    const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
    class AtelierNavItem extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-at-dd-toggle]');
        this.panel = this.querySelector('[data-at-dd-panel]');
        if (!this.button || !this.panel) return;
        this.timer = null;

        this.onEnter = () => {
          if (!hoverable.matches || !desktop.matches) return;
          this.later(() => this.open(), 90);
        };
        this.onLeave = () => {
          if (!hoverable.matches) return;
          // stays open while keyboard focus is inside it
          this.later(() => {
            let keyboard = false;
            try {
              keyboard = !!this.querySelector(':focus-visible');
            } catch (e) {}
            if (!keyboard) this.close();
          }, 220);
        };
        this.onClick = (event) => {
          event.preventDefault();
          clearTimeout(this.timer);
          this.isOpen() ? this.close() : this.open();
        };
        this.onKeydown = (event) => {
          if (event.key !== 'Escape' || !this.isOpen()) return;
          event.preventDefault();
          const inside = this.panel.contains(document.activeElement);
          this.close();
          if (inside || document.activeElement === this.button) this.button.focus();
        };
        this.onFocusOut = (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
        };
        this.onDocDown = (event) => {
          if (this.isOpen() && !this.contains(event.target)) this.close();
        };
        this.onMedia = () => !desktop.matches && this.close();

        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusout', this.onFocusOut);
        this.button.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('pointerdown', this.onDocDown);
        onMedia(desktop, this.onMedia);
      }

      disconnectedCallback() {
        if (!this.button || !this.panel) return;
        clearTimeout(this.timer);
        this.removeEventListener('mouseenter', this.onEnter);
        this.removeEventListener('mouseleave', this.onLeave);
        this.removeEventListener('focusout', this.onFocusOut);
        this.button.removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('pointerdown', this.onDocDown);
        offMedia(desktop, this.onMedia);
      }

      later(fn, ms) {
        clearTimeout(this.timer);
        this.timer = setTimeout(fn, ms);
      }

      isOpen() {
        return this.button.getAttribute('aria-expanded') === 'true';
      }

      open() {
        clearTimeout(this.timer);
        if (this.isOpen()) return;
        document.querySelectorAll('atelier-nav-item.is-open').forEach((item) => item !== this && item.close && item.close());
        this.button.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
      }

      close() {
        clearTimeout(this.timer);
        if (!this.isOpen()) return;
        this.button.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
      }
    }
    customElements.define('atelier-nav-item', AtelierNavItem);
  }

  if (!customElements.get('atelier-cart-reviews')) {
    class AtelierCartReviews extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-at-crev-track]');
        this.cards = [...this.querySelectorAll('[data-at-crev-card]')];
        if (!this.track || this.cards.length < 2) return;

        this.queued = false;
        this.onScroll = () => {
          if (this.queued) return;
          this.queued = true;
          requestAnimationFrame(() => {
            this.queued = false;
            this.update();
          });
        };
        this.onClick = (event) => {
          if (this.dragged) {
            event.preventDefault();
            this.dragged = false;
            return;
          }
          const card = event.target.closest('[data-at-crev-card]');
          if (card && !card.classList.contains('is-current')) this.go(card);
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('click', this.onClick);
        this.bindDrag();
        this.classList.add('is-ready');
        this.update();
      }

      current() {
        const box = this.track.getBoundingClientRect();
        let best = this.cards[0];
        let most = -1;
        this.cards.forEach((card) => {
          const r = card.getBoundingClientRect();
          const seen = Math.min(r.right, box.right) - Math.max(r.left, box.left);
          if (seen > most + 1) {
            most = seen;
            best = card;
          }
        });
        return best;
      }

      update() {
        const cur = this.current();
        this.cards.forEach((card) => card.classList.toggle('is-current', card === cur));
      }

      go(card) {
        const left = card.offsetLeft - this.cards[0].offsetLeft;
        this.track.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }

      // Mouse drag (touch and trackpads scroll natively).
      bindDrag() {
        let drag = null;
        const end = (event) => {
          if (!drag) return;
          const { moved, start, dx } = drag;
          drag = null;
          this.track.classList.remove('is-dragging');
          if (event && this.track.hasPointerCapture && this.track.hasPointerCapture(event.pointerId)) {
            this.track.releasePointerCapture(event.pointerId);
          }
          if (!moved) return;
          this.dragged = true;
          setTimeout(() => (this.dragged = false), 0);
          let target = this.current();
          const i = this.cards.indexOf(start);
          if (target === start && Math.abs(dx) > 40) {
            target = this.cards[Math.max(0, Math.min(this.cards.length - 1, i + (dx < 0 ? 1 : -1)))];
          }
          this.go(target);
        };
        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          drag = { x: event.clientX, left: this.track.scrollLeft, moved: false, dx: 0, start: this.current(), id: event.pointerId };
        });
        this.track.addEventListener('pointermove', (event) => {
          if (!drag || event.pointerId !== drag.id) return;
          drag.dx = event.clientX - drag.x;
          if (!drag.moved) {
            if (Math.abs(drag.dx) < 5) return;
            drag.moved = true;
            this.track.setPointerCapture(event.pointerId);
            this.track.classList.add('is-dragging');
          }
          event.preventDefault();
          this.track.scrollLeft = drag.left - drag.dx;
        });
        this.track.addEventListener('pointerup', end);
        this.track.addEventListener('pointercancel', end);
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }
    }
    customElements.define('atelier-cart-reviews', AtelierCartReviews);
  }
})();
